import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import resolveImageUrl from "@/lib/imageUrl";

// Public archive page for Social Run for Spain.
// Consumes /api/social-run. Renders one component per section `template`.
// The layouts intentionally vary in rhythm — hero fullbleed, editorial
// grids, split image, stats, quote — so scrolling feels like reading an
// editorial, not a repetitive card feed.

// External-safe CTA: React Router's <Link to> does not handle absolute
// URLs. This helper picks the right element so http(s) links open in a
// new tab as real anchors, while internal paths still use client routing.
const CTA = ({ to, className, children, ...rest }) => {
  const external = typeof to === "string" && /^https?:\/\//i.test(to);
  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={className} {...rest}>
        {children}
      </a>
    );
  }
  return <Link to={to} className={className} {...rest}>{children}</Link>;
};

export default function SocialRunPage({ sections, seo }) {
  const { lang } = useI18n();
  const T = (obj) => (obj && (obj[lang] || obj.en || obj.da)) || "";

  // Update <title> + meta so social sharing looks right without a full SSR.
  useEffect(() => {
    if (seo?.title) document.title = seo.title;
    if (seo?.description) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
      m.setAttribute("content", seo.description);
    }
  }, [seo]);

  // Pre-fetch collaborators once — sections filter locally by slug/tier.
  const [collabs, setCollabs] = useState([]);
  const [ambassadors, setAmbassadors] = useState([]);
  useEffect(() => {
    api.get("/content", { params: { kind: "collaborator", status: "published" } }).then(r => setCollabs(r.data || [])).catch(() => {});
    api.get("/ambassadors").then(r => setAmbassadors(r.data.ambassadors || [])).catch(() => {});
  }, []);

  return (
    <div className="bg-base text-ink" data-testid="social-run-page">
      {/* Descriptive H1 — always present so the recap has exactly one H1
          even if the hero section is hidden or its headline is empty. */}
      <h1 className="sr-only" data-testid="sr-page-h1">Social Run for Spain 2026</h1>
      {sections.map((s) => (
        <SectionRenderer key={s.id} s={s.data} T={T} collabs={collabs} ambassadors={ambassadors} />
      ))}
    </div>
  );
}

function SectionRenderer({ s, T, collabs, ambassadors }) {
  const template = s.template || "editorial_text";
  const props = { s, T, collabs, ambassadors };
  switch (template) {
    case "hero":              return <HeroBlock {...props} />;
    case "editorial_grid":    return <EditorialGrid {...props} />;
    case "split_image":       return <SplitImage {...props} />;
    case "stats":             return <StatsBlock {...props} />;
    case "editorial_text":    return <EditorialText {...props} />;
    case "collaborators":     return <CollaboratorsBlock {...props} />;
    case "ambassadors":       return <AmbassadorsBlock {...props} />;
    case "full_bleed_quote":  return <FullBleedQuote {...props} />;
    case "final_cta":         return <FinalCTA {...props} />;
    case "auction_archive":   return <AuctionArchive {...props} />;
    default:                  return <EditorialText {...props} />;
  }
}

const focalPos = (im) => `${im?.focal?.x ?? 50}% ${im?.focal?.y ?? 50}%`;

function Body({ text }) {
  if (!text) return null;
  return String(text).replace(/\r\n/g, "\n").split(/\n{2,}/).map(s => s.trim()).filter(Boolean).map((para, i) => (
    <p key={i} className="mt-4 text-[15px] md:text-[17px] leading-relaxed whitespace-pre-line text-ink/85">{para}</p>
  ));
}

// ─── HERO ───────────────────────────────────────────────────────────────
function HeroBlock({ s, T }) {
  const img = s.images?.[0] || {};
  const showHeadline = s.show_headline !== false;
  return (
    <section className="relative min-h-[92vh] flex items-end overflow-hidden bg-ink" data-testid="sr-hero">
      {img.url && (
        <img src={resolveImageUrl(img.url)} alt={T(img.alt) || ""} loading="eager"
             className="absolute inset-0 w-full h-full object-cover"
             style={{ objectPosition: focalPos(img) }} />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/60 to-ink/20" />
      <div className="relative max-editorial container-x pb-16 md:pb-24 text-white">
        <div className="label-mono text-terracotta tracking-[0.22em] text-[10px] md:text-[11px]">{T(s.eyebrow)}</div>
        {s.meta && <div className="label-mono text-white/70 mt-1 text-[10px] tracking-[0.22em]">{T(s.meta)}</div>}
        {showHeadline && (
          <h2 data-testid="sr-hero-headline" className="mt-4 font-editorial text-[44px] md:text-[80px] leading-[0.95] tracking-tighter max-w-4xl">{T(s.headline)}</h2>
        )}
        <div className={`${showHeadline ? "mt-6" : "mt-4"} max-w-2xl text-white/85`}><Body text={T(s.body)} /></div>
      </div>
    </section>
  );
}

// ─── EDITORIAL_GRID ─────────────────────────────────────────────────────
function EditorialGrid({ s, T }) {
  const imgs = (s.images || []).filter(i => i?.url);
  return (
    <section className="max-editorial container-x py-20 md:py-28" data-testid={`sr-grid-${s.slug}`}>
      <div className="max-w-3xl">
        <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
        <h2 className="mt-3 font-editorial text-[32px] md:text-[46px] leading-[1] tracking-tighter">{T(s.headline)}</h2>
        {T(s.body) && <div className="mt-4"><Body text={T(s.body)} /></div>}
      </div>
      {imgs.length > 0 && (
        <div className="mt-12 grid grid-cols-6 gap-3 md:gap-5">
          {imgs.map((im, i) => (
            <div key={i} className={`${i === 0 ? "col-span-6 md:col-span-4 aspect-[16/10]"
                                      : i === 1 ? "col-span-3 md:col-span-2 aspect-square"
                                      : i === 2 ? "col-span-3 md:col-span-2 aspect-square"
                                      : "col-span-6 md:col-span-2 aspect-[4/3]"} overflow-hidden`}>
              <img src={resolveImageUrl(im.url)} alt={T(im.alt) || ""} loading="lazy"
                   className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-[900ms]"
                   style={{ objectPosition: focalPos(im) }} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── SPLIT_IMAGE ────────────────────────────────────────────────────────
function SplitImage({ s, T }) {
  const img = s.images?.[0];
  return (
    <section className="max-editorial container-x py-16 md:py-24 grid md:grid-cols-12 gap-8 md:gap-14" data-testid={`sr-split-${s.slug}`}>
      <div className="md:col-span-5 md:sticky md:top-24 self-start">
        <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
        <h2 className="mt-3 font-editorial text-[28px] md:text-[40px] leading-[1.05] tracking-tighter">{T(s.headline)}</h2>
        {(s.cta_label && s.cta_url) && (
          <CTA to={s.cta_url} data-testid={`sr-cta-${s.slug}`}
                className="mt-6 inline-flex items-center gap-1.5 bg-terracotta text-white px-5 py-2.5 text-sm tracking-wide hover:bg-terracotta-dark">
            {T(s.cta_label)} <ArrowRight size={14} strokeWidth={1.8} />
          </CTA>
        )}
        {(s.cta2_label && s.cta2_url) && (
          <CTA to={s.cta2_url} className="ml-3 mt-6 inline-flex items-center gap-1.5 border border-ink text-ink px-5 py-2.5 text-sm tracking-wide hover:bg-ink hover:text-white">
            {T(s.cta2_label)}
          </CTA>
        )}
      </div>
      <div className="md:col-span-7">
        {img?.url && (
          <div className="aspect-[4/5] md:aspect-[3/4] overflow-hidden mb-6">
            <img src={resolveImageUrl(img.url)} alt={T(img.alt) || ""} loading="lazy"
                 className="w-full h-full object-cover"
                 style={{ objectPosition: focalPos(img) }} />
          </div>
        )}
        <div className="max-w-xl"><Body text={T(s.body)} /></div>
      </div>
    </section>
  );
}

// ─── STATS ──────────────────────────────────────────────────────────────
function StatsBlock({ s, T }) {
  const stats = s.stats || [];
  return (
    <section className="max-editorial container-x py-16 md:py-24" data-testid={`sr-stats-${s.slug}`}>
      <div className="max-w-3xl">
        <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
        {T(s.headline) && <h2 className="mt-3 font-editorial text-[32px] md:text-[54px] leading-[1] tracking-tighter">{T(s.headline)}</h2>}
        {T(s.body) && <div className="mt-4"><Body text={T(s.body)} /></div>}
      </div>
      <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10 border-t border-line/70">
        {stats.map((st, i) => (
          <div key={i} className="pt-8">
            <div className="font-editorial text-[36px] md:text-[54px] leading-[0.95] tracking-tighter text-ink">{st.value}</div>
            <div className="mt-2 text-[12px] uppercase tracking-[0.18em] text-ink/60">{T(st.label)}</div>
          </div>
        ))}
      </div>
      {(s.cta_label && s.cta_url) && (
        <CTA to={s.cta_url} data-testid={`sr-stats-cta-${s.slug}`}
              className="mt-10 inline-flex items-center gap-1.5 border border-ink text-ink px-5 py-2.5 text-sm tracking-wide hover:bg-ink hover:text-white">
          {T(s.cta_label)} <ArrowRight size={14} strokeWidth={1.8} />
        </CTA>
      )}
    </section>
  );
}

// ─── EDITORIAL_TEXT ─────────────────────────────────────────────────────
function EditorialText({ s, T }) {
  return (
    <section className="max-editorial container-x py-16 md:py-24" data-testid={`sr-text-${s.slug}`}>
      <div className="max-w-3xl mx-auto">
        <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
        {T(s.headline) && <h2 className="mt-3 font-editorial text-[32px] md:text-[46px] leading-[1.02] tracking-tighter">{T(s.headline)}</h2>}
        <div className="mt-4"><Body text={T(s.body)} /></div>
      </div>
    </section>
  );
}

// ─── COLLABORATORS ──────────────────────────────────────────────────────
function CollaboratorsBlock({ s, T, collabs }) {
  const chosen = (s.collaborator_slugs || []).filter(Boolean);
  const bySlug = (slug) => collabs.find(c => c.data?.slug === slug);
  let list = [];
  if (chosen.length) {
    list = chosen.map(bySlug).filter(Boolean);
  } else if (s.collaborator_fallback_tier) {
    list = collabs.filter(c => (c.data?.tier || "supporter") === s.collaborator_fallback_tier && c.data?.logo);
  }
  if (!list.length && !T(s.body)) return null;
  return (
    <section className="max-editorial container-x py-16 md:py-24" data-testid={`sr-collabs-${s.slug}`}>
      <div className="max-w-3xl">
        <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
        <h2 className="mt-3 font-editorial text-[28px] md:text-[40px] leading-[1.05] tracking-tighter">{T(s.headline)}</h2>
        {T(s.body) && <div className="mt-4 max-w-2xl"><Body text={T(s.body)} /></div>}
      </div>
      {list.length > 0 && (
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-8 items-center">
          {list.map(c => {
            const slug = c.data?.slug || c.id;
            return (
              <Link key={c.id} to={`/get-involved#partner-${slug}`} data-testid={`sr-collab-${slug}`}
                    className="flex items-center justify-center h-16 md:h-20 grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-500">
                <img src={resolveImageUrl(c.data.logo)} alt={c.data.name}
                     className="max-h-full max-w-full object-contain" />
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ─── AMBASSADORS ────────────────────────────────────────────────────────
function AmbassadorsBlock({ s, T, ambassadors }) {
  const chosen = (s.ambassador_slugs || []).filter(Boolean);
  const list = chosen.length
    ? chosen.map(slug => ambassadors.find(a => a.slug === slug)).filter(Boolean)
    : ambassadors;
  if (!list.length && !T(s.body)) return null;
  return (
    <section className="max-editorial container-x py-16 md:py-24" data-testid={`sr-ambassadors-${s.slug}`}>
      <div className="max-w-3xl">
        <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
        <h2 className="mt-3 font-editorial text-[28px] md:text-[40px] leading-[1.05] tracking-tighter">{T(s.headline)}</h2>
        {T(s.body) && <div className="mt-4 max-w-2xl"><Body text={T(s.body)} /></div>}
      </div>
      {list.length > 0 && (
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {list.map(a => (
            <figure key={a.id} className="text-ink">
              {a.photo && (
                <div className="aspect-[3/4] overflow-hidden bg-line/10">
                  <img src={resolveImageUrl(a.photo)} alt={T(a.alt) || a.name} className="w-full h-full object-cover" />
                </div>
              )}
              <figcaption className="mt-3">
                <div className="font-editorial text-lg leading-tight">{a.name}</div>
                {T(a.role) && <div className="text-[12px] text-ink/60 mt-0.5">{T(a.role)}</div>}
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── FULL_BLEED_QUOTE ───────────────────────────────────────────────────
function FullBleedQuote({ s, T }) {
  const img = s.images?.[0];
  return (
    <section className="relative overflow-hidden bg-ink text-white" data-testid={`sr-quote-${s.slug}`}>
      {img?.url && (
        <img src={resolveImageUrl(img.url)} alt={T(img.alt) || ""} loading="lazy"
             className="absolute inset-0 w-full h-full object-cover opacity-45"
             style={{ objectPosition: focalPos(img) }} />
      )}
      <div className="relative max-editorial container-x py-24 md:py-36">
        <div className="max-w-3xl">
          <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
          <h2 className="mt-4 font-editorial text-[36px] md:text-[64px] leading-[1] tracking-tighter">{T(s.headline)}</h2>
          <div className="mt-6 text-white/85"><Body text={T(s.body)} /></div>
        </div>
      </div>
    </section>
  );
}

// ─── FINAL_CTA ──────────────────────────────────────────────────────────
function FinalCTA({ s, T }) {  const img = s.images?.[0];
  return (
    <section className="relative overflow-hidden bg-ink text-white" data-testid="sr-final">
      {img?.url && (
        <img src={resolveImageUrl(img.url)} alt={T(img.alt) || ""} loading="lazy"
             className="absolute inset-0 w-full h-full object-cover opacity-40"
             style={{ objectPosition: focalPos(img) }} />
      )}
      <div className="relative max-editorial container-x py-24 md:py-36 text-center">
        <h2 className="font-editorial text-[36px] md:text-[64px] leading-[1] tracking-tighter">{T(s.headline)}</h2>
        {T(s.body) && <p className="mt-4 text-white/80 text-lg">{T(s.body)}</p>}
        {(s.cta_label && s.cta_url) && (
          <CTA to={s.cta_url} data-testid="sr-final-cta"
                className="mt-8 inline-flex items-center gap-1.5 bg-terracotta hover:bg-terracotta-dark text-white px-6 py-3 tracking-wide">
            {T(s.cta_label)} <ArrowRight size={16} strokeWidth={1.8} />
          </CTA>
        )}
      </div>
    </section>
  );
}

// ─── AUCTION_ARCHIVE (historical, past tense) ───────────────────────────
// Relocated from /raffle. Renders the two signed football shirts auctioned
// on 6 September 2026 during Social Run for Spain.
function AuctionArchive({ s, T }) {
  const items = s.items || [];
  return (
    <section className="max-editorial container-x py-16 md:py-24" data-testid={`sr-auction-${s.slug}`}>
      <div className="max-w-3xl">
        <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
        {T(s.headline) && (
          <h2 className="mt-3 font-editorial text-[28px] md:text-[40px] leading-[1.05] tracking-tighter">
            {T(s.headline)}
          </h2>
        )}
        {T(s.body) && <div className="mt-4 max-w-2xl"><Body text={T(s.body)} /></div>}
      </div>
      {items.length > 0 && (
        <div className="mt-10 grid sm:grid-cols-2 gap-5 md:gap-6" data-testid="sr-auction-items">
          {items.map((it, i) => (
            <article key={i} className="border border-line rounded-xl bg-white overflow-hidden" data-testid={`sr-auction-item-${i}`}>
              {it.image && (
                <div className="aspect-[4/3] bg-line/10 flex items-center justify-center">
                  <img src={resolveImageUrl(it.image)} alt={T(it.alt) || ""} loading="lazy"
                       className="w-full h-full object-contain" />
                </div>
              )}
              <div className="p-5">
                <h3 className="font-editorial text-lg leading-tight">{T(it.title)}</h3>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

